import { useTranslation } from 'react-i18next';
import ComponentWithLoader from '../ComponentWithLoader/ComponentWithLoader';
import { EmptyDataPlaceholder } from '../ui/empty-data-placeholder';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { BaseModalRef } from '@/app/helpers/types';
import TrashIcon from '@/app/assets/icons/trash.svg?react';
import EditIcon from '@/app/assets/icons/edit.svg?react';
import { checkRole, getFetchEror } from '@/app/helpers';
import { useNotification } from '../contexts/NotificationContext';
import ConfirmDeletion from '../confirm-deletion';
import { UITable } from '../ui/ui-table';
import { EmployeeModal } from './EmployeeModal';
import {
  useEditCompanyEmployeeMutation,
  useGetLinkedUsersQuery,
} from '@/entities/didox/api';
import { useAppSelector } from '@/app/hooks';
import { eimzo } from '@/entities/eimzo';
import { useGetFactorsBranchesQuery } from '@/app/api';
import AlertIcon from '@/app/assets/icons/alert.svg?react';

export interface EmployeeRow {
  id: string;
  index: number;
  name: string;
  pinfl: string | null;
  role: RoleValues;
  branch: {
    id: number;
    name: string;
  } | null;
  rules: string[];
}

export type RoleValues = 19000 | 19001 | 19002;

const permissions: Record<RoleValues, string[]> = {
  19002: [
    'add_deal_conditions',
    'set_limits',
    'sign_documents',
    'add_payments',
    'view_all_sections',
    'approve_requests',
    'reject_requests',
  ],
  19001: [
    'add_deal_conditions',
    'set_limits',
    'approve_requests',
    'reject_requests',
    'add_payments',
    'view_all_sections',
  ],
  19000: [
    'add_branches',
    'add_deal_conditions',
    'set_limits',
    'approve_requests',
    'reject_requests',
    'sign_documents',
    'add_payments',
    'view_all_sections',
  ],
};
export const FactoringEmployees = () => {
  const { t, i18n } = useTranslation();
  const { profile } = useAppSelector((state) => state.auth);
  const { showNotification } = useNotification();
  const { isManager, isBranchEmploye } = checkRole(profile?.roles);

  const lang = i18n.language.split('_')[0] || 'ru';

  const employeeModalRef = useRef<BaseModalRef>(null);

  const [employeeToEdit, setEmployeeToEdit] = useState<EmployeeRow | null>(
    null
  );
  const [employeeToDelete, setEmployeeToDelete] = useState<EmployeeRow | null>(
    null
  );
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const [confirmOpen, setConfirmOpen] = useState(false);

  const ALLOWED_ROLES = new Set<RoleValues>([19000, 19001, 19002]);

  const [editCompanyEmployee] = useEditCompanyEmployeeMutation();

  const { data, isLoading: isEmployeesLoading } = useGetLinkedUsersQuery({
    lang,
  });

  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleDeleteEmployee = async () => {
    try {
      if (!profile?.tin || !employeeToDelete) return;

      const basePayload = {
        tin: profile.tin,
        fio: employeeToDelete.name,
        fiztin: employeeToDelete.pinfl,
      };

      const gnkpermissions = await eimzo.sign(
        JSON.stringify({ ...basePayload, roles: [] }),
        profile.tin
      );

      const internalpermissions = await eimzo.sign(
        JSON.stringify({ ...basePayload, roles: [], branch: null }),
        profile.tin
      );

      await editCompanyEmployee({
        gnkpermissions,
        internalpermissions,
        is_director: 0,
      }).unwrap();

      showNotification(t('settings.employee_successfully_deleted'), 'success');
    } catch (error) {
      showNotification(getFetchEror(error), 'error');
    }
  };

  const { data: branches } = useGetFactorsBranchesQuery();

  const employeesData: EmployeeRow[] = useMemo(() => {
    if (!data) return [];

    return Object.entries(data).reduce<EmployeeRow[]>((acc, [id, user]) => {
      const rolesArray = Object.values(user.roles ?? {});

      const matchedRole = rolesArray.find((role) =>
        ALLOWED_ROLES.has(Number(role.code) as RoleValues)
      );

      if (!matchedRole) return acc;

      const roleCode = Number(matchedRole.code) as RoleValues;

      const branch = branches?.data.find(
        (el) => String(el.unique) === matchedRole?.branch
      );
      acc.push({
        id,
        index: acc.length + 1,
        name: user.name,
        pinfl: user.pinfl,
        branch: branch
          ? {
              id: branch.unique,
              name: branch.branchName,
            }
          : null,
        role: roleCode,
        rules: permissions[roleCode],
      });

      return acc;
    }, []);
  }, [data]);

  const handleAddEmployee = () => {
    setEmployeeToEdit(null);
    employeeModalRef.current?.open();
  };

  const handleEditEmployee = (row: EmployeeRow) => {
    setEmployeeToEdit(row);
    employeeModalRef.current?.open();
  };

  const askDeleteEmployee = (row: EmployeeRow) => {
    setEmployeeToDelete(row);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    await handleDeleteEmployee();
    setConfirmOpen(false);
    setEmployeeToDelete(null);
  };

  const handleModalSuccess = () => {
    setEmployeeToEdit(null);
  };

  return (
    <>
      <ComponentWithLoader isLoading={false}>
        {isManager || isBranchEmploye ? (
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{ p: 1, background: '#E1E7FA', borderRadius: '8px' }}
          >
            <Box component={AlertIcon} />
            <Typography
              sx={{ fontSize: '15px', fontWeight: 500, color: '#0B1370' }}
            >
              {t('settings.employees.cannot_add_users_text')}
            </Typography>
          </Stack>
        ) : (
          <Stack direction='row' justifyContent='space-between'>
            <Typography fontSize='1.7rem' fontWeight={600}>
              {t('settings.employees.title')}
            </Typography>

            <Button color='inherit' onClick={handleAddEmployee}>
              {t('settings.employees.add_employee')}
            </Button>
          </Stack>
        )}
        <EmptyDataPlaceholder
          title={t('settings.employees.empty_data_title')}
          subtitle={t('settings.employees.empty_data_subtitle')}
          isEmpty={!employeesData.length}
          isLoading={isEmployeesLoading}
        >
          <UITable<EmployeeRow>
            data={employeesData}
            isLoading={isEmployeesLoading}
            sx={{
              mt: 1,
              '& .MuiTableHead-root': {
                color: 'rgba(11, 19, 112, 1)',
                '& .MuiTableCell-root': {
                  color: 'text.secondary',
                },
              },
            }}
            columns={[
              {
                label: t('settings.employees.table_labels.number'),
                render: (row) => (
                  <Typography
                    sx={{ fontSize: '1.3rem', color: 'text.secondary' }}
                  >
                    {row.index}
                  </Typography>
                ),
              },
              {
                label: t('settings.employees.table_labels.pinfl_inn'),
                render: (row) => (
                  <Typography sx={{ fontSize: '1.3rem', color: '#0B1370' }}>
                    {row.pinfl}
                  </Typography>
                ),
              },
              {
                label: t('settings.employees.table_labels.full_name'),
                render: (row) => (
                  <Typography sx={{ fontSize: '1.3rem', color: '#0B1370' }}>
                    {row.name}
                  </Typography>
                ),
              },
              {
                label: t('settings.employees.table_labels.role'),
                render: (row) => (
                  <Typography
                    sx={{
                      fontSize: '1.3rem',
                      color: '#0B1370',
                      fontWeight: 600,
                    }}
                  >
                    {t(`roles.${row.role}`)}
                  </Typography>
                ),
              },
              {
                label: t('settings.employees.table_labels.branch'),
                render: (row) => (
                  <Typography sx={{ fontSize: '1.3rem', color: '#0B1370' }}>
                    {row.branch?.name ?? '-'}
                  </Typography>
                ),
              },
              {
                label: t('settings.employees.table_labels.permissions'),
                render: (row) => {
                  const isExpanded = expandedRows[row.id];

                  const visibleRules = isExpanded
                    ? row.rules
                    : row.rules.slice(0, 3);

                  return (
                    <Stack>
                      {visibleRules.map((rule, i) => (
                        <Typography
                          sx={{
                            color: '#0B1370',
                          }}
                          key={i}
                        >
                          - {t(`rules.${rule}`)}
                        </Typography>
                      ))}

                      {row.rules.length > 3 && (
                        <Typography
                          sx={{
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#1555FC',
                            mt: 0.5,
                          }}
                          onClick={() => toggleExpand(row.id)}
                        >
                          {isExpanded ? t('common:collapse') : t('common:more')}
                        </Typography>
                      )}
                    </Stack>
                  );
                },
              },
              {
                label: '',
                render: (row) =>
                  (!isManager || !isBranchEmploye) && (
                    <Stack direction='row' spacing={2}>
                      <Box
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleEditEmployee(row)}
                      >
                        <EditIcon />
                      </Box>

                      <Box
                        sx={{ cursor: 'pointer' }}
                        onClick={() => askDeleteEmployee(row)}
                      >
                        <TrashIcon />
                      </Box>
                    </Stack>
                  ),
              },
            ]}
          />
        </EmptyDataPlaceholder>
      </ComponentWithLoader>

      <EmployeeModal
        ref={employeeModalRef}
        employee={employeeToEdit}
        onSuccess={handleModalSuccess}
      />

      <ConfirmDeletion
        open={confirmOpen}
        onSubmit={confirmDelete}
        onClose={() => setConfirmOpen(false)}
        type='employee'
      />
    </>
  );
};
